import React, { useLayoutEffect, useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Orb, ORB_KEYFRAMES, StateKey } from '../components/DriftOrb';
import DriftUIShowcase from '../components/DriftUIShowcase';
import DriftReflections from '../components/DriftReflections';
import './DriftProject.css';

const ORB_SEQUENCE: { state: StateKey; label: string; text: string }[] = [
  { state: 'baseline',      label: 'Baseline',      text: 'this is you.' },
  { state: 'withdrawal',    label: 'Withdrawal',    text: 'you, when you are withdrawn.' },
  { state: 'numbness',      label: 'Numbness',      text: 'you, when you are numb.' },
  { state: 'fragmentation', label: 'Fragmentation', text: 'you, when you are fragmented.' },
  { state: 'agitation',     label: 'Agitation',     text: 'you, when you are agitated.' },
];

interface DriftProjectProps {
  theme?: string;
}

const DriftProject: React.FC<DriftProjectProps> = ({ theme = 'light' }) => {
  // ── Orb scroll section ──────────────────────────────────────
  const orbSectionRef = useRef<HTMLDivElement>(null);
  const [orbPhase, setOrbPhase]       = useState<number>(-1); // -1=intro, 0-4=orb states
  const [orbState, setOrbState]       = useState<StateKey>('baseline');
  const [textVisible, setTextVisible] = useState(false);
  const [currentText, setCurrentText] = useState('');
  const [currentLabel, setCurrentLabel] = useState('');
  const textTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Inject orb keyframes once
  useEffect(() => {
    const el = document.createElement('style');
    el.textContent = ORB_KEYFRAMES;
    document.head.appendChild(el);
    return () => { document.head.removeChild(el); };
  }, []);

  // Scroll → phase (with hysteresis to prevent rapid flipping at boundaries)
  useEffect(() => {
    const phaseRef = { current: -2 }; // -2 = uninitialized
    let rafId = 0;

    const compute = () => {
      if (!orbSectionRef.current) return;
      const rect = orbSectionRef.current.getBoundingClientRect();
      const scrolled = -rect.top;
      const total = orbSectionRef.current.offsetHeight - window.innerHeight;
      const progress = Math.max(0, Math.min(1, scrolled / total));

      let newPhase: number;
      if (progress < 0.13) {
        newPhase = -1;
      } else {
        newPhase = Math.min(4, Math.floor((progress - 0.13) / (0.87 / 5)));
      }

      if (newPhase !== phaseRef.current) {
        phaseRef.current = newPhase;
        setOrbPhase(newPhase);
      }
    };

    const handleScroll = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(compute);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    compute();
    return () => {
      window.removeEventListener('scroll', handleScroll);
      cancelAnimationFrame(rafId);
    };
  }, []);

  // Phase → orb state + text (with cross-fade)
  useEffect(() => {
    if (orbPhase < 0) {
      setTextVisible(false);
      return;
    }
    const step = ORB_SEQUENCE[orbPhase];

    // Fade text out, swap content + orb state, fade text back in
    setTextVisible(false);
    if (textTimeoutRef.current) clearTimeout(textTimeoutRef.current);
    // orb state change fires immediately — its own CSS transitions handle the visual morph
    setOrbState(step.state);
    // text content swaps after the caption has faded out
    textTimeoutRef.current = setTimeout(() => {
      setCurrentText(step.text);
      setCurrentLabel(step.label);
      setTextVisible(true);
    }, 500);
  }, [orbPhase]);

  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useLayoutEffect(() => {
    document.body.className = 'light drift-page';
    return () => {
      document.body.className = theme;
    };
  }, [theme]);

  const fadeInUp = {
    hidden: { opacity: 0, y: 40 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] as const }
    }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.15 }
    }
  };

  return (
    <div className="drift-project">

      {/* ── 1. HERO ─────────────────────────────────────────────── */}
      <section className="drift-hero">

        {/* Scattered posters */}
        <motion.img
          src="/content/Drift/images/hero/Image2.png"
          alt="" className="drift-poster drift-poster-1" aria-hidden="true"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1], delay: 0.0 }}
        />
        <motion.img
          src="/content/Drift/images/hero/Perspective Pin 1.png"
          alt="" className="drift-poster drift-poster-2" aria-hidden="true"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1], delay: 0.12 }}
        />
        <motion.img
          src="/content/Drift/images/hero/Graphic Design Illustration 1.png"
          alt="" className="drift-poster drift-poster-3" aria-hidden="true"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
        />
        <motion.img
          src="/content/Drift/images/hero/Image.png"
          alt="" className="drift-poster drift-poster-5" aria-hidden="true"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1], delay: 0.08 }}
        />

        {/* Centered logo + tagline */}
        <motion.div
          className="drift-hero-content"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1], delay: 0.35 }}
        >
          <img
            src="/content/Drift/drift logo.svg"
            alt="Drift"
            className="drift-hero-logo"
          />
          <p className="drift-hero-sub">
            An attention-awareness app that makes the invisible visible.
          </p>
          <div className="drift-hero-meta">
            <span>Speculative Design</span>
            <span className="drift-dot">·</span>
            <span>Vibe Coding</span>
            <span className="drift-dot">·</span>
            <span>Visual Design</span>
          </div>
        </motion.div>

      </section>

      {/* ── 1.5 FIGBUILD BANNER ─────────────────────────────────── */}

      {/* Static ribbon */}
      <div className="drift-ribbon" aria-hidden="true">
        <span>FigBuild 2026</span>
        <span className="drift-ribbon-dot">·</span>
        <span>Figma's Student Design-a-thon</span>
        <span className="drift-ribbon-dot">·</span>
        <span>March 6–9</span>
        <span className="drift-ribbon-dot">·</span>
        <span>$30,000 in prizes</span>
        <span className="drift-ribbon-dot">·</span>
        <span>Figma Make</span>
        <span className="drift-ribbon-dot">·</span>
        <span>72 hours</span>
      </div>

      {/* Context section */}
      <motion.section
        className="drift-figbuild"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: '-60px' }}
        variants={staggerContainer}
      >
        <div className="drift-container drift-figbuild-grid">

          {/* Left — competition context */}
          <div className="drift-figbuild-left">
            <motion.p className="drift-section-label" variants={fadeInUp}>Competition Context</motion.p>
            <motion.div className="drift-figbuild-logo-wrap" variants={fadeInUp}>
              <img src="/content/Drift/figbuild-logo.png" alt="FigBuild 2026" className="drift-figbuild-logo" />
            </motion.div>
            <motion.p className="drift-figbuild-body" variants={fadeInUp}>
              Figma's second annual design-a-thon for students. Teams of 2–4 competed
              across the U.S., Canada, India, and Australia, building a digital product
              around a speculative design prompt over one weekend.
            </motion.p>
            <motion.div className="drift-figbuild-stats" variants={fadeInUp}>
              <div className="drift-stat">
                <span className="drift-stat-value">72 hrs</span>
                <span className="drift-stat-label">Build window</span>
              </div>
              <div className="drift-stat">
                <span className="drift-stat-value">$30K</span>
                <span className="drift-stat-label">Total prizes</span>
              </div>
              <div className="drift-stat">
                <span className="drift-stat-value">Mar 6–9</span>
                <span className="drift-stat-label">2026</span>
              </div>
            </motion.div>
          </div>

          {/* Right — build approach */}
          <motion.div className="drift-figbuild-right" variants={fadeInUp}>
            <p className="drift-section-label">How it was built</p>
            <p className="drift-figbuild-build-headline">Vibe coded,<br/>start to finish.</p>
            <p className="drift-figbuild-build-body">
              No templates, no pre-built kits. Drift was designed and built entirely
              through AI-assisted workflows. A working prototype shipped in a single weekend sprint.
            </p>
            <div className="drift-figbuild-tools">
              <span>Figma Make</span>
              <span>Claude</span>
              <span>Cursor</span>
              <span>Figma Design</span>
            </div>

            <p className="drift-section-label" style={{ marginTop: '32px', marginBottom: '12px' }}>Team</p>
            <div className="drift-figbuild-team">
              <a href="https://www.linkedin.com/in/meetshashi/" target="_blank" rel="noopener noreferrer" className="drift-team-btn">Shahsidhara Narayappa ↗</a>
              <a href="https://www.linkedin.com/in/vivek-d--/" target="_blank" rel="noopener noreferrer" className="drift-team-btn">Vivek Das ↗</a>
              <a href="https://www.linkedin.com/in/tanishqsardar/" target="_blank" rel="noopener noreferrer" className="drift-team-btn">Tanishq Sardar ↗</a>
            </div>
          </motion.div>

        </div>
      </motion.section>

      {/* ── 2. PROMPT / PROBLEM STATEMENT ──────────────────────── */}
      <motion.section
        className="drift-section drift-section-prompt"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: '-80px' }}
        variants={staggerContainer}
      >
        <div className="drift-container">
          <motion.p className="drift-section-label" variants={fadeInUp}>02 — The Prompt</motion.p>

          <motion.blockquote className="drift-prompt-quote" variants={fadeInUp}>
            Design a tool that tracks, measures, visualizes or quantifies an aspect of human
            sensory experience. Within the tool, provide the ability to detect, enhance,
            or manipulate those same sensory inputs.
          </motion.blockquote>

          <motion.div className="drift-prompt-grid" variants={fadeInUp}>
            <div className="drift-prompt-block">
              <p className="drift-prompt-block-label">Context</p>
              <p className="drift-prompt-block-body">
                The "quantified self" movement has transformed how we understand our bodies and behaviors.
                From step counters to sleep trackers to mood apps, we're only scratching the surface.
                Think about designers working on the first generation of fitness trackers in the early
                2000s, imagining interfaces for heart rate monitoring and GPS tracking long
                before the sensors existed to make it real. Just as those early designers speculated
                about technological possibilities, your task is to imagine the next frontier of
                self-knowledge and design for capabilities that don't yet exist.
              </p>
            </div>
            <div className="drift-prompt-block">
              <p className="drift-prompt-block-label">Your Mission</p>
              <p className="drift-prompt-block-body">
                Identify something intangible, invisible, or previously unmeasurable about the human
                sensory experience and design a speculative tool to track and influence it. This tool
                should support a wellness goal or behavioral change for an individual or a group.
              </p>
              <p className="drift-prompt-block-label" style={{ marginTop: '32px' }}>Design Requirements</p>
              <p className="drift-prompt-block-body">
                Your solution should feel novel and future forward. Not a remix of an existing app,
                and not something you generate on the first go. This is a speculative design challenge
                rooted in human need.
              </p>
            </div>
          </motion.div>
        </div>
      </motion.section>

      {/* ── 3. THE CONCEPT ──────────────────────────────────────── */}
      <motion.section
        className="drift-section drift-concept"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: '-80px' }}
        variants={staggerContainer}
      >
        <div className="drift-concept-inner">

          {/* Left — poetic text */}
          <div className="drift-concept-text">
            <motion.p className="drift-section-label" variants={fadeInUp}>03 — The Concept</motion.p>

            <motion.p className="drift-concept-line drift-concept-line-open" variants={fadeInUp}>
              There is a sense the human body has always had,<br />
              but never been able to read.
            </motion.p>

            <motion.p className="drift-concept-line drift-concept-line-mid" variants={fadeInUp}>
              The body's ability to perceive how close or far it is<br />
              from its own characteristic self.
            </motion.p>

            <motion.p className="drift-concept-line drift-concept-line-comparison" variants={fadeInUp}>
              Like balance tells you where you are in space,<br />
              <span>this sense tells you where you are relative to yourself.</span>
            </motion.p>

            <motion.div className="drift-concept-punches" variants={fadeInUp}>
              <span>Until now, it existed only as vague unease.</span>
              <span>Felt too late.</span>
              <span>Never read.</span>
            </motion.div>

            <motion.p className="drift-concept-reveal" variants={fadeInUp}>
              We call it<br />The Axiometric Sense.
            </motion.p>
          </div>

          {/* Right — abstract image */}
          <motion.div
            className="drift-concept-image-wrap"
            variants={fadeInUp}
          >
            <img
              src="/content/Drift/images/axiometric.jpeg"
              alt=""
              className="drift-concept-image"
              aria-hidden="true"
            />
          </motion.div>

        </div>
      </motion.section>

      {/* ── 4. THE PROBLEM ──────────────────────────────────────── */}
      <motion.section
        className="drift-problem"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: '-80px' }}
        variants={staggerContainer}
      >
        {/* Top-left image */}
        <motion.div className="drift-problem-img drift-problem-img-tl" variants={fadeInUp}>
          <img src="/content/Drift/images/Feeling1.jpeg" alt="" aria-hidden="true" />
        </motion.div>

        {/* Bottom-right image */}
        <motion.div className="drift-problem-img drift-problem-img-br" variants={fadeInUp}>
          <img src="/content/Drift/images/Feeling.jpeg" alt="" aria-hidden="true" />
        </motion.div>

        {/* Centered text */}
        <div className="drift-problem-body">
          <motion.p className="drift-section-label" variants={fadeInUp}>04 — The Problem</motion.p>

          <motion.p className="drift-problem-hook" variants={fadeInUp}>
            We feel off too.
          </motion.p>

          <motion.p className="drift-problem-sub" variants={fadeInUp}>
            And so do a billion other people.
          </motion.p>

          <motion.div className="drift-problem-stats" variants={fadeInUp}>
            <div className="drift-problem-stat">
              <span className="drift-problem-stat-num">1B+</span>
              <span className="drift-problem-stat-text">people globally live with some form of mental health condition</span>
            </div>
            <div className="drift-problem-stat">
              <span className="drift-problem-stat-num">82%</span>
              <span className="drift-problem-stat-text">of employees are at risk of burnout</span>
            </div>
            <div className="drift-problem-stat">
              <span className="drift-problem-stat-num">25</span>
              <span className="drift-problem-stat-text">the age Gen Z is hitting peak exhaustion</span>
            </div>
          </motion.div>

          <motion.div className="drift-problem-poem" variants={fadeInUp}>
            <p>Yet most never saw it coming.</p>
            <p>You don't fall apart all at once. You drift.</p>
            <p className="drift-problem-poem-quiet">Slowly. Invisibly.</p>
            <p>Before you feel burnout, your eyes stop reaching for beauty.</p>
            <p>Before you feel anxious, your hands start escaping to your phone.</p>
            <p>Before you feel lost, your world quietly gets smaller.</p>
            <p className="drift-problem-poem-close">The signal was always there.<br />Nobody was reading it.</p>
          </motion.div>
        </div>

      </motion.section>

      {/* ── 5. MEERA ────────────────────────────────────────────── */}
      <motion.section
        className="drift-meera"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: '-80px' }}
        variants={staggerContainer}
      >
        {/* Left — image */}
        <motion.div className="drift-meera-image" variants={fadeInUp}>
          <img src="/content/Drift/images/meera.jpeg" alt="Meera" />
        </motion.div>

        {/* Right — text */}
        <div className="drift-meera-text">
          <motion.p className="drift-section-label" variants={fadeInUp}>05 — A Story</motion.p>

          <motion.h2 className="drift-meera-headline" variants={fadeInUp}>
            This is happening<br />to Meera.
          </motion.h2>

          <motion.p className="drift-meera-bio" variants={fadeInUp}>
            A UX Researcher in London.<br />
            After a difficult year, she goes through every day with unusual efficiency.
          </motion.p>

          <motion.div className="drift-meera-contrasts" variants={fadeInUp}>
            <div className="drift-meera-line drift-meera-still">
              <span className="drift-meera-marker" />
              She still produces good work.
            </div>
            <div className="drift-meera-line drift-meera-still">
              <span className="drift-meera-marker" />
              She still shows up.
            </div>
            <div className="drift-meera-line drift-meera-doesnt">
              <span className="drift-meera-marker drift-meera-marker-dim" />
              She doesn't feel depressed.
            </div>
            <div className="drift-meera-line drift-meera-doesnt">
              <span className="drift-meera-marker drift-meera-marker-dim" />
              She doesn't feel anxious.
            </div>
            <div className="drift-meera-line drift-meera-final">
              <span className="drift-meera-marker drift-meera-marker-dim" />
              She used to look out of windows.
            </div>
            <div className="drift-meera-line drift-meera-final drift-meera-fade">
              <span className="drift-meera-marker drift-meera-marker-dim" />
              Now she doesn't.
            </div>
          </motion.div>
        </div>
      </motion.section>

      {/* ── 6. MEET DRIFT — sticky orb scroll ───────────────────── */}
      <div className="drift-orb-scroll" ref={orbSectionRef}>
        <div className="drift-orb-sticky">

          {/* Intro overlay — visible before phase 0 */}
          <div className={`drift-orb-intro ${orbPhase < 0 ? 'is-visible' : ''}`}>
            <p className="drift-section-label">06 — The Product</p>
            <img
              src="/content/Drift/drift logo.svg"
              alt="drift"
              className="drift-orb-intro-logo"
            />
            <p className="drift-orb-intro-sub">see yourself before you disappear.</p>
          </div>

          {/* Orb — mounted once, transitions seamlessly */}
          <div className={`drift-orb-stage ${orbPhase >= 0 ? 'is-visible' : ''}`}>
            <Orb stateKey={orbState} />
          </div>

          {/* State label + text — fades between phases */}
          <div className={`drift-orb-caption ${orbPhase >= 0 && textVisible ? 'is-visible' : ''}`}>
            <span className="drift-orb-caption-label">{currentLabel}</span>
            <p className="drift-orb-caption-text">{currentText}</p>
          </div>

          {/* Progress dots */}
          <div className="drift-orb-dots">
            {ORB_SEQUENCE.map((_, i) => (
              <div
                key={i}
                className={`drift-orb-dot ${orbPhase === i ? 'is-active' : ''}`}
              />
            ))}
          </div>

        </div>
      </div>

      {/* ── 7. UI SHOWCASE ──────────────────────────────────────── */}
      <DriftUIShowcase />

      {/* ── 8. MEERA'S STORY ────────────────────────────────────── */}
      <motion.section
        className="drift-meera-story"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: '-80px' }}
        variants={staggerContainer}
      >
        <div className="drift-container">
          <motion.p className="drift-section-label" variants={fadeInUp}>08 — Meera's Story</motion.p>
          <motion.h2 className="drift-ms-headline" variants={fadeInUp}>
            Drift saw what Meera couldn't.
          </motion.h2>
        </div>

        <motion.div className="drift-ms-timeline" variants={fadeInUp}>
          {[
            { day: 'Day 1',  state: 'baseline'     as StateKey, label: 'Baseline',     score: 12, copy: 'Meera feels fine. Drift score is low. She moves on.' },
            { day: 'Day 4',  state: 'withdrawal'   as StateKey, label: 'Withdrawal',   score: 21, copy: 'Pull signal rises. Phone pickups double. She hasn\'t noticed.' },
            { day: 'Day 7',  state: 'agitation'    as StateKey, label: 'Agitation',    score: 29, copy: 'Friction spikes at 3pm three days running. Something is building.' },
            { day: 'Day 9',  state: 'numbness'     as StateKey, label: 'Numbness',     score: 34, copy: 'Five signals below baseline. She still tells everyone she\'s fine.' },
            { day: 'Day 10', state: 'baseline'     as StateKey, label: 'Baseline',     score: 24, copy: 'Drift suggests one gap before the evening. She tries it. Score drops.' },
          ].map((item, i) => (
            <div className="drift-ms-col" key={i}>
              {/* Orb */}
              <div className="drift-ms-orb-wrap">
                <Orb stateKey={item.state} />
              </div>

              {/* Divider */}
              <div className="drift-ms-divider" />

              {/* Day */}
              <span className="drift-ms-day">{item.day}</span>

              {/* State label */}
              <span className="drift-ms-state">{item.label}</span>

              {/* Score badge */}
              <div className="drift-ms-score">
                <span>{item.score}</span>
                <span className="drift-ms-score-unit">drift</span>
              </div>

              {/* Copy */}
              <p className="drift-ms-copy">{item.copy}</p>
            </div>
          ))}
        </motion.div>
      </motion.section>

      {/* ── 10. REFLECTIONS ─────────────────────────────────────── */}
      <DriftReflections />

    </div>
  );
};

export default DriftProject;
